/**
 * Disposes GPU/CPU resources held by Three.js object hierarchies so backing
 * {@link ArrayBuffer}s for {@link THREE.BufferAttribute}s can be collected.
 *
 * Ownership-aware: pass {@link DisposerOptions.isProtected} to skip resources
 * shared via the cache, freeing an actor's private resources without nuking shared ones.
 */

import * as THREE from 'three';

import { SceneComponent } from '../components/index.js';

import type { IDisposer } from '../interfaces.js';

/** Options controlling which resources a subtree walk disposes. */
export interface DisposerOptions {
  /** Returns `true` for resources to PROTECT from disposal (e.g. cache-owned). Defaults to disposing all. */
  isProtected?: (resource: object) => boolean;
  /** Dedupes resources shared between nodes (e.g. cloned meshes sharing geometry). Created per call when omitted. */
  seen?: Set<object>;
}

/** Walks an Object3D hierarchy and disposes geometry, materials, textures, and related GPU resources. */
export class Disposer implements IDisposer {
  private readonly isProtected?: (resource: object) => boolean;
  private readonly seen: Set<object>;

  constructor(options: DisposerOptions = {}) {
    this.isProtected = options.isProtected;
    this.seen = options.seen ?? new Set<object>();
  }

  /** Disposes a resource once, unless already seen or protected. */
  private disposeResource(resource: { dispose: () => void } | null | undefined): void {
    if (!resource || this.seen.has(resource) || (this.isProtected?.(resource) ?? false)) {
      return;
    }
    this.seen.add(resource);
    resource.dispose();
  }

  /**
   * Disposes a material plus its texture-valued properties: `Material.dispose()` does
   * not free texture maps (they are often shared), so we walk them explicitly.
   */
  private disposeMaterial(material: THREE.Material | null | undefined): void {
    if (!material || this.seen.has(material) || (this.isProtected?.(material) ?? false)) {
      return;
    }
    this.seen.add(material);
    for (const value of Object.values(material)) {
      if (value instanceof THREE.Texture) {
        this.disposeResource(value);
      }
    }
    material.dispose();
  }

  /** Disposes geometry, materials and their textures on a single node. Honors `isProtected`. */
  disposeNode(object: THREE.Object3D): void {
    if (object instanceof SceneComponent) {
      object.dispose(this);
      return;
    }

    if (object instanceof THREE.BatchedMesh) {
      if (!(this.isProtected?.(object) ?? false)) {
        object.dispose();
      }
      return;
    }

    if (
      object instanceof THREE.Mesh ||
      object instanceof THREE.Line ||
      object instanceof THREE.LineSegments ||
      object instanceof THREE.LineLoop ||
      object instanceof THREE.Points ||
      object instanceof THREE.Sprite
    ) {
      this.disposeResource(object.geometry);
    }

    if (object instanceof THREE.SkinnedMesh) {
      object.skeleton?.dispose();
    }

    if (object instanceof THREE.Light) {
      const shadow = (object as THREE.Light & { shadow?: THREE.LightShadow }).shadow;
      shadow?.map?.dispose();
    }

    const withMaterial = object as THREE.Object3D & {
      material?: THREE.Material | THREE.Material[];
    };
    const mat = withMaterial.material;
    if (mat) {
      const materials = Array.isArray(mat) ? mat : [mat];
      for (const m of materials) {
        if (m instanceof THREE.Material) {
          this.disposeMaterial(m);
        }
      }
    }
  }

  /** Post-order dispose under `root` (children before parents). */
  disposeSubtree(root: THREE.Object3D): void {
    const snapshot = root.children.slice();
    for (const child of snapshot) {
      this.disposeSubtree(child);
    }
    this.disposeNode(root);
  }
}

/** Disposes geometry, materials and their textures on a single node. Honors `isProtected`. */
export function disposeObject3DNode(object: THREE.Object3D, options: DisposerOptions = {}): void {
  new Disposer(options).disposeNode(object);
}

/** Post-order dispose under `root` (children before parents). Pass `isProtected` to skip cache-owned resources. */
export function disposeObject3DSubtree(root: THREE.Object3D, options: DisposerOptions = {}): void {
  new Disposer(options).disposeSubtree(root);
}
